import { initializeApiConfig } from '@foundation/apis/ApiConfig';
import { AuthProvider } from '@shared/auth';
import {
  ErrorBoundary,
  setupGlobalErrorHandlers,
} from '@shared/error-boundaries';
import {
  createI18nextInstance,
  TranslationsProvider,
} from '@shared/localization';
import { UserManager } from 'oidc-client-ts';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { SomethingWentWrong } from '@/components/common/errors/general-errors/SomethingWentWrong';
import { APP_URL } from '@/utils/env';

import { App } from './App';
import './styles/globals.css';

setupGlobalErrorHandlers();

initializeApiConfig({
  paymentsApiUrl: (import.meta.env.VITE_PAYMENT_API ?? '').replace(/\/$/, ''),
});

const urlNonceToken =
  new URLSearchParams(window.location.search).get('nonceToken') ?? undefined;
if (urlNonceToken) {
  window.sessionStorage.setItem('pmi.payments.nt', urlNonceToken);
}
const nonceToken =
  urlNonceToken ??
  window.sessionStorage.getItem('pmi.payments.nt') ??
  undefined;

const userManager = new UserManager({
  authority: import.meta.env.VITE_IDSRV_API || '',
  client_id: import.meta.env.VITE_IDSRV_CLIENT_ID || '',
  redirect_uri: `${APP_URL}/${import.meta.env.VITE_IDSRV_REDIRECT_ROUTE}`,
  response_type: 'code',
  scope: import.meta.env.VITE_IDSRV_SCOPE,
  loadUserInfo: true,
  acr_values: nonceToken ? `otp:${nonceToken}` : undefined,
  automaticSilentRenew: !nonceToken,
});

async function bootstrap() {
  const i18nInstance = await createI18nextInstance({
    namespaces: ['payments'],
    lazyLoadResources: true,
    resourceLoader: async (language) => {
      const module = await import(
        `./localization/translations/${language}.json`
      );
      return module.default;
    },
  });

  createRoot(document.getElementById('root') as HTMLElement).render(
    <StrictMode>
      <ErrorBoundary fallback={<SomethingWentWrong />}>
        <TranslationsProvider i18nInstance={i18nInstance}>
          <AuthProvider userManager={userManager}>
            <App />
          </AuthProvider>
        </TranslationsProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
}

bootstrap();
